import { faAngleLeft, faShoppingBag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from 'components/ui/Button/IconButton';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import VariationDot from 'components/ui/VariationDot';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { ImageExtension, PriceInformation, PriceValue, ShopListProduct } from 'microshop-api';
import { darken } from 'polished';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { selectQuickViewDetails, toggleQuickView } from 'store/reducers/productSlice';
import styled, { ThemeWebColors } from 'styled-components';
import getImage, { ImageType } from 'utils/getImage';
import ProductPrice from './ProductPrice';

export type CardProduct = ShopListProduct;

interface IProductCardProps {
    product?: CardProduct;
    onBuy?: (ref: React.MutableRefObject<HTMLElement | null>) => void;
    category?: any;
    categories?: any[];
    categoryPath?: any;
    gtm?: boolean;
}

const ProductCard: React.FC<IProductCardProps> = (props) => {
    const { product } = props;
    const dispatch = useAppDispatch();
    const { quickView, quickViewProduct, categories } = useAppSelector(({ product, assortment }) => ({
        ...product,
        ...assortment,
    }));
    const quickViewVariation = useAppSelector(selectQuickViewDetails).quickViewVariation;
    const isCartAvailable = true;

    if (!product) return null;
    const featuredColor =
        (quickViewProduct?.productNumber === product?.productNumber && quickViewVariation?.colorCode) ||
        (product?.hasVariations && product?.variations?.find((v) => v.featured)?.colorCode) ||
        undefined;
    const isSelected = quickView && product.productNumber === quickViewProduct?.productNumber;
    var selectedCategoryKey = Object.keys(categories).find((key) => categories[key].code === product.nodeId);
    var selectedCategory = selectedCategoryKey ? categories[selectedCategoryKey].slug : undefined;

    return (
        <GenericProductCard
            {...props}
            product={product}
            featuredColor={undefined}
            isSelected={isSelected}
            category={selectedCategory}
            onBuy={(ref) => {
                dispatch(
                    toggleQuickView({
                        category: product.nodeId ?? '',
                        pnrOrSlug: product.slug ?? product.productNumber!,
                        featuredColor,
                    }),
                );
                props.onBuy && props.onBuy(ref);
            }}
            disableBuyButton={!isCartAvailable}
            priceText={undefined}
            showRetail={true}
        />
    );
};

type ProductCardProps = IProductCardProps & {
    featuredColor?: string;
    overrideImage?: string | null;
    isSelected?: boolean;
    product?: CardProduct;
    rows?: JSX.Element;
    wishlist?: { show: boolean; saved: boolean };
    priceText?: string;
    disableBuyButton?: boolean;
    showRetail?: boolean;
    actions?: JSX.Element;
    imgBg?: boolean;
};
export const GenericProductCard = ({
    product,
    onBuy,
    category,
    categories,
    categoryPath,
    gtm,
    featuredColor,
    isSelected = false,
    overrideImage,
    rows,
    wishlist,
    priceText,
    disableBuyButton,
    showRetail,
    actions,
    imgBg,
}: ProductCardProps) => {
    const [showAllColors, setShowAllColors] = useState(false);
    const showBrand = true;
    const cardRef = useRef<HTMLDivElement>(null);

    if (!product) return null;

    const colors = product.hasVariations
        ? product.variations?.map((f) => f.webColors?.map((wc) => wc.toLocaleLowerCase() as keyof ThemeWebColors))
        : [];

    const productLink = category ? `/product/${category}/${product.slug}` : `/product/${product.slug}`;

    return (
        <Card
            selected={isSelected}
            ref={cardRef}
            className="p-1 overflow-hidden"
            itemProp="ListItem"
            itemScope
            itemType="https://schema.org/Product"
        >
            <meta itemProp="productID" content={product.productNumber || ''} />
            <Link to={productLink}>
                <ImageContainer>
                    <ImageWrapper imgBg={imgBg}>
                        <StyledImage
                            itemProp="image"
                            src={overrideImage ?? getImage(product?.image, ImageType.Preview, ImageExtension.Jpg)}
                            alt={product?.productName || 'product'}
                        />
                    </ImageWrapper>
                </ImageContainer>
            </Link>
            <ActionsContainer justify="between" className="pt-3 px-3">
                <div></div>
            </ActionsContainer>
            <Body column justify="start" className="pt-1 px-2 pb-2 flex-grow-1">
                <div className="px-2 flex-grow-1">
                    <Flex justify="between">
                        <Link className="link-dark" to={productLink}>
                            <Text itemProp="name" large={product && product.productName!?.length < 30}>
                                {product.productName ?? 'Test produkt'}
                                <meta itemProp="name" content={`${product.productName}`} />
                            </Text>
                        </Link>
                    </Flex>
                    <Flex>
                        {showBrand && (
                            <>
                                <Text light small>
                                    {product.productBrandName ?? 'Brand'}
                                    <meta itemProp="brand" content={`${product.productBrandName}`} />
                                </Text>
                                <Text className="mx-1" light small>
                                    {'/'}
                                </Text>
                            </>
                        )}
                        <Text className="text-break" light small>
                            {product.productInternalNumber ? product.productInternalNumber : product.productNumber}
                        </Text>
                    </Flex>
                    {rows && rows}
                    <Flex justify="between">
                        {colors && colors.length > 0 ? (
                            <ColorsContainer align="center" className="my-1">
                                {colors?.map(
                                    (color, i) =>
                                        (showAllColors || i < 5) && (
                                            <Color align="center" key={i} itemProp="color" className="me-1">
                                                <VariationDot colors={color} />{' '}
                                            </Color>
                                        ),
                                )}
                                {colors && colors.length > 5 && (
                                    <Color className="me-1">
                                        <ExpandColors onClick={() => setShowAllColors(!showAllColors)}>
                                            {showAllColors ? (
                                                <FontAwesomeIcon icon={faAngleLeft} />
                                            ) : (
                                                `+ ${colors.length - 5}`
                                            )}
                                        </ExpandColors>
                                    </Color>
                                )}
                            </ColorsContainer>
                        ) : null}
                    </Flex>
                </div>
                {!disableBuyButton ? (
                    <AddToCart
                        bgColor="fill"
                        square
                        color="price"
                        className="w-100"
                        onClick={() => {
                            onBuy && onBuy(cardRef);
                        }}
                        icon={faShoppingBag}
                    >
                        <PriceText
                            priceText={priceText}
                            usePoints={product.usePoints ?? false}
                            price={product.price}
                            fallbackPrice={product.fallbackPrice}
                        />
                    </AddToCart>
                ) : (
                    <PriceText
                        priceText={priceText}
                        usePoints={product.usePoints ?? false}
                        disabled
                        commerceType={null}
                    />
                )}
            </Body>
        </Card>
    );
};

type PriceTextProps = {
    priceText?: string;
    price?: PriceValue;
    fallbackPrice?: PriceInformation;
    usePoints: boolean;
    disabled?: boolean;
    commerceType?: any;
};
const PriceText = ({ priceText, price, disabled, usePoints, fallbackPrice }: PriceTextProps): JSX.Element => {
    const { t } = useTranslation();
    const text = (text?: string) => {
        return (
            <Text color={disabled ? 'textLight' : 'price'} fontSize={12} className="f1-400">
                {typeof text === 'string' ? text : t('cart.add_one', 'Add to cart')}
            </Text>
        );
    };

    if (typeof priceText === 'string') return text(priceText);
    return (
        <>
            <ProductPrice
                price={price!}
                fallbackPrice={fallbackPrice!}
                usePoints={usePoints}
                compact
                color={disabled ? 'textLight' : 'price'}
            />
            <span className="visually-hidden">{t('cart.add_one', 'Add to cart')}</span>
        </>
    );
};

const Card = styled.div<{ selected: boolean }>`
    flex: 1;
    ${({ theme, selected }) =>
        selected
            ? `border: ${darken(0.1, theme.colors.fillMedium)} 1px solid`
            : `border: ${theme.colors.fill} 1px solid`};
    background-color: white;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    position: relative;
`;
const ImageContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    position: relative;
    overflow: hidden;

    :before {
        padding-bottom: 100%;
        display: block;
        content: '';
        width: 100%;
    }
`;

const ImageWrapper = styled.div<{ imgBg?: boolean }>`
    ${({ theme, imgBg }) => imgBg && `background-color: ${theme.colors.transparentBg};`}
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
`;

const StyledImage = styled.img`
    ${({ theme }) => theme.media.md} {
        height: calc(100% - 40px);
        width: calc(100% - 70px);
        top: 35px;
        left: 35px;
    }
    position: absolute;
    object-fit: contain;
    height: calc(100% - 45px);
    width: calc(100% - 16px);
    top: 35px;
    left: 8px;
`;

const Body = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    text-align: left;
    width: 100%;
`;

const ActionsContainer = styled(Flex)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
`;

const ColorsContainer = styled(Flex)`
    flex-wrap: wrap;
`;

const Color = styled(Flex)`
    height: 21px;
`;

const AddToCart = styled(IconButton)``;

const ExpandColors = styled.button`
    font-size: 12px;
    background: none;
    border: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.transparent};
    &:hover {
        border-bottom: 1px solid ${({ theme }) => theme.colors.text};
    }
`;
export default ProductCard;
